/**
 * Invoice PDF Generator
 *
 * Generates PDF invoices using pdfmake, replacing the Kendo-based DOM-to-PDF approach.
 * Supports both Detailed (D) and Narrative (N) modes, as well as court invoices.
 */

import type { Content, ContentText } from 'pdfmake/interfaces';
import {
    PdfDocument,
    createHeader,
    createTable,
    createAccountSummary,
    createTrustAccounting,
    createTwoColumn,
    createBarcodeSection,
    buildAccountSummaryRows,
    buildTrustAccountingData,
    getInvoiceStyles,
    PDF_COLORS,
} from '@/lib/pdf';
import { parseQuillHtml, parseHtmlContent } from '@/lib/pdf/utils/parseQuillHtml';
import { loadFontsForPdf } from '@/lib/pdf/fonts';
import type {
    PdfGenerationResult,
    PdfTableColumn,
    LetterheadData,
    ClientInfoData,
    QrCodeData,
    TrustTransaction,
} from '@/lib/pdf';
import type Invoice from '@/entities/invoice';
import type Task from '@/entities/task';
import type Expense from '@/entities/expense';
import type Contact from '@/entities/contact';
import type Matter from '@/entities/matter';
import type Lead from '@/entities/lead';
import type Transaction from '@/entities/transaction';
import type User from '@/entities/user';
import FinancialHelper, { InvoiceInfo } from '../FinancialHelper';
import type { FinalValues } from '@/@types/invoice';
import OptionHelper from '@/Configuration/GeneralOptions/OptionHelper';
import CaseHelper from '@/Case/CaseHelper';
import AddressHelper from '@/Contacts/Address/AddressHelper';
import { formatFML, formatFMLFromId } from '@/Contacts/ContactFormat';
import { formatUserName } from '@/Contacts/ContactFormat';
import { formatDuration, roundDurationToMinutes } from '@/lib/picos/duration';
import { getBillableRate, getCost } from '@/TaskManagement/TaskHelper';
import * as DateUtil from '@/lib/dateutils';
import { stripHtml } from 'string-strip-html';
import { getCurrentFirm, getCurrentFirmId } from '@/lib/picos/currentLogin';
import { encodeBase62 } from '@/lib/picos/uuids';
import { getAddresses, getEmails } from '@/lib/picos/getResourceTypes';
import paymentApi from '../PaymentProcessor/paymentApi';
import SOCKET_OPTIONS from '@/lib/socket-variables';
import find from '@/store/find';
import store from '@/store/store';
import LookupRegistry from '@/ui/LookupSelect/LookupRegistry';
import {resolveDraftAddressees, resolveFrozenAddressee} from './invoiceAddressee';

/**
 * Options for invoice PDF generation
 */
export interface InvoicePdfOptions {
    /** Invoice mode: Narrative (N) or Detailed (D) */
    mode: 'N' | 'D';
    /** Generate a court invoice (simplified format) */
    courtInvoice?: boolean;
}

/**
 * Transaction type labels for trust accounting
 */
const TX_TYPE_LABELS = new Map<string, string>([
    ['FR', 'Trust Deposit'],
    ['FO', 'Split Payment'],
    ['PT', 'Payment from Trust'],
    ['PC', 'Payment from Client'],
    ['ST', 'Manual Balance'],
    ['SA', 'Manual Balance'],
    ['TT', 'Trust Transfer'],
    ['DE', 'Escrow Distribution'],
    ['ED', 'Escrow Deposit'],
    ['RR', 'Refund Retainer'],
    ['DT', 'Trust Disbursement'],
    ['BA', 'Adjustment'],
]);

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Format date for display (MM/DD/YYYY)
 */
function formatDate(date: Date | string | null | undefined): string {
    if (!date) return '';
    // short=false gives 4-digit year (YYYY), short=true gives 2-digit year (YY)
    return DateUtil.formatDate(date, false, false, false) || '';
}

/**
 * Get user initials from user ID.
 * Uses first/middle/last from the linked Contact entity for 3-letter
 * initials when a middle name is available (e.g., "PCJ" for Patricia Claire Jones).
 */
function getUserInitials(userId: string | null | undefined): string {
    if (!userId) return '';
    const user = find<User>('users', userId);
    if (!user) return '';

    // Look up the linked Contact for structured name fields
    if (user.contactId) {
        const contact = find<Contact>('contacts', user.contactId);
        if (contact) {
            let initials = '';
            if (contact.first) initials += contact.first[0];
            if (contact.middle) initials += contact.middle[0];
            if (contact.last) initials += contact.last[0];
            if (initials) return initials.toUpperCase();
        }
    }

    // Fallback: split formatted name on spaces
    const name = formatUserName(user);
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
}

/**
 * Convert HTML to plain text while preserving structure (bullets, line breaks).
 * Exported for the plain-text invoice email body (see InvoiceHelper). OWA-506.
 */
export function cleanText(text: string): string {
    if (!text) return '';

    let cleaned = text;

    // Convert list items to bullet points
    cleaned = cleaned.replace(/<li[^>]*>/gi, '• ');
    cleaned = cleaned.replace(/<\/li>/gi, '\n');

    // Convert line breaks and block elements to newlines
    cleaned = cleaned.replace(/<br\s*\/?>/gi, '\n');
    cleaned = cleaned.replace(/<\/p>/gi, '\n');
    cleaned = cleaned.replace(/<\/div>/gi, '\n');
    cleaned = cleaned.replace(/<\/tr>/gi, '\n');

    // Remove remaining HTML tags
    cleaned = cleaned.replace(/<[^>]+>/g, '');

    // Decode common HTML entities
    cleaned = cleaned
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&bull;/g, '•')
        .replace(/&middot;/g, '·')
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
        .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)));

    // Clean up multiple newlines and trim
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    return cleaned.trim();
}

/**
 * Parse HTML letterhead to plain text lines
 */
function parseLetterhead(html: string): LetterheadData {
    // Simple HTML parsing - strip tags and get lines
    const cleaned = html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/?(div|p)[^>]*>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .trim();

    const lines = cleaned.split('\n').map(l => l.trim()).filter(Boolean);

    return {
        html,
        firmName: lines[0] || getCurrentFirm().legalName,
        tagline: lines[1],
        address: lines.slice(2).join(' • '),
    };
}

/**
 * Build client info section content
 */
function buildClientInfo(
    invoice: Invoice,
    clients: Contact[],
    matter: Matter | null,
    lead: Lead | null
): Content[] {
    const content: Content[] = [];
    const optionHelper = new OptionHelper();

    // Addressee: an optional per-case bill-to contact (e.g. a third-party payer such as
    // Texas Legal) overrides the client(s) as the invoice recipient. See OWA-320.
    const addressees = resolveDraftAddressees(clients, matter);

    // Recipient name(s)
    const clientNames = addressees.map(c => c.billingName || formatFML(c)).join(', ');
    content.push({
        text: clientNames,
        style: 'clientName',
    });

    // Recipient address
    const address = AddressHelper.getDeliveryAddress(addressees);
    if (address) {
        // convertAddressToParagraph returns a string when useHtml is false
        const addressLines = AddressHelper.convertAddressToParagraph(address, false) as string;
        if (addressLines) {
            content.push({
                text: addressLines,
                style: 'clientInfoValue',
                margin: [0, 0, 0, 10],
            });
        }
    }

    // Invoice details in two-column label/value format
    const rows: [Content, Content][] = [];

    rows.push([
        { text: 'Invoice', style: 'clientInfoValue', bold: true },
        { text: invoice.invoiceNumber, style: 'invoiceNumber' },
    ]);

    // Matter/Lead name
    if (matter) {
        rows.push([
            { text: 'Matter', style: 'clientInfoValue', bold: true },
            { text: matter.name, style: 'clientInfoValue' },
        ]);
    } else if (lead) {
        const consultName = optionHelper.getOptionValue(OptionHelper.PAID_CONSULT_NAME);
        rows.push([
            { text: 'Service', style: 'clientInfoValue', bold: true },
            { text: consultName || 'Consultation', style: 'clientInfoValue' },
        ]);
    }

    // Dates
    rows.push([
        { text: 'Invoice Date', style: 'clientInfoValue', bold: true },
        { text: formatDate(invoice.issueDate), style: 'clientInfoValue' },
    ]);

    if (invoice.dueDate) {
        rows.push([
            { text: 'Due Date', style: 'clientInfoValue', bold: true },
            { text: formatDate(invoice.dueDate), style: 'clientInfoValue' },
        ]);
    }

    // Period. A lead (consult) draft has no From date; the calculation ignores a saved
    // one, so the PDF must not print it either (OWA-940). Finalized invoices are unchanged.
    const isLeadDraft = !!invoice.leadId && !(invoice.finalizedOn != null && invoice.invoiceStatus === 'F');
    if (invoice.startDate && invoice.endDate && !isLeadDraft) {
        rows.push([
            { text: 'Period', style: 'clientInfoValue', bold: true },
            { text: `${formatDate(invoice.startDate)} - ${formatDate(invoice.endDate)}`, style: 'clientInfoValue' },
        ]);
    }

    content.push({
        table: {
            widths: ['auto', '*'],
            body: rows,
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 0],
    } as Content);

    return content;
}

/**
 * Build client info from frozen display metadata (for finalized invoices with snapshots).
 */
function buildClientInfoFromFrozen(
    invoice: Invoice,
    display: FinalValues['display']
): Content[] {
    const content: Content[] = [];
    const optionHelper = new OptionHelper();

    // Addressee from snapshot: a frozen per-case bill-to contact overrides the client(s)
    // as the invoice recipient (OWA-320). Both name and address come pre-resolved.
    const addressee = resolveFrozenAddressee(display);

    // Recipient name(s) from snapshot
    content.push({
        text: addressee.name,
        style: 'clientName',
    });

    // Recipient address from snapshot
    if (addressee.address) {
        content.push({
            text: addressee.address,
            style: 'clientInfoValue',
            margin: [0, 0, 0, 10],
        });
    }

    // Invoice details
    const rows: [Content, Content][] = [];

    rows.push([
        { text: 'Invoice', style: 'clientInfoValue', bold: true },
        { text: invoice.invoiceNumber, style: 'invoiceNumber' },
    ]);

    if (display.matters.length > 0) {
        rows.push([
            { text: 'Matter', style: 'clientInfoValue', bold: true },
            { text: display.matters[0].name, style: 'clientInfoValue' },
        ]);
    } else if (display.leadName) {
        const consultName = optionHelper.getOptionValue(OptionHelper.PAID_CONSULT_NAME);
        rows.push([
            { text: 'Service', style: 'clientInfoValue', bold: true },
            { text: consultName || 'Consultation', style: 'clientInfoValue' },
        ]);
    }

    rows.push([
        { text: 'Invoice Date', style: 'clientInfoValue', bold: true },
        { text: formatDate(invoice.issueDate), style: 'clientInfoValue' },
    ]);

    if (invoice.dueDate) {
        rows.push([
            { text: 'Due Date', style: 'clientInfoValue', bold: true },
            { text: formatDate(invoice.dueDate), style: 'clientInfoValue' },
        ]);
    }

    if (invoice.startDate && invoice.endDate) {
        rows.push([
            { text: 'Period', style: 'clientInfoValue', bold: true },
            { text: `${formatDate(invoice.startDate)} - ${formatDate(invoice.endDate)}`, style: 'clientInfoValue' },
        ]);
    }

    content.push({
        table: {
            widths: ['auto', '*'],
            body: rows,
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 0],
    } as Content);

    return content;
}

/**
 * Build detailed labor table content
 */
function buildDetailedLaborTable(
    tasks: Task[],
    invoice: Invoice
): Content {
    // Filter tasks
    const filteredTasks = tasks.filter(t => !t.hideFromInvoice);

    if (filteredTasks.length === 0) {
        return { text: '' };
    }

    const columns: PdfTableColumn[] = [
        { header: 'Date', field: 'date', width: 62, noWrap: true },
        { header: 'User', field: 'user', width: 30, noWrap: true },
        { header: 'Notes', field: 'notes', width: '*', isHtml: true },
        { header: 'Time', field: 'time', width: 38, alignment: 'right', noWrap: true },
        { header: 'Rate', field: 'rate', width: 62, alignment: 'right', isCurrency: true, noWrap: true },
        { header: 'Cost', field: 'cost', width: 75, alignment: 'right', isCurrency: true, noWrap: true },
    ];

    // Build data rows — use pre-resolved names from frozen snapshot when available
    const data = filteredTasks.map(task => {
        const taskAny = task as any;
        const initials = taskAny._finalAssignedToInitials ?? getUserInitials(task.assignedTo || task.createdBy);

        // Pass raw HTML — the Table component handles isHtml rendering
        const notes = task.invoiceNotes || task.title || '';

        // Get the cost using the invoice's billing settings (or pre-resolved cost from snapshot)
        const cost = taskAny._finalCost ?? getCost(task, invoice);

        return {
            date: formatDate(task.completionDate),
            user: initials,
            notes,
            time: formatDuration(task.elapsedTime, 'sdecimal', task), // 'sdecimal' = just number, 'decimal' = number + " hours"
            rate: task.associatedRate || 0,
            cost,
        };
    });

    // Calculate totals
    const totalCost = data.reduce((sum, row) => sum + (row.cost as number), 0);
    const footer = {
        date: '',
        user: '',
        notes: 'Total',
        time: '',
        rate: '',
        cost: totalCost,
    };

    return createTable({
        columns,
        data,
        showHeader: true,
        alternatingRows: false,
        showTotals: true,
        footer,
    });
}

/**
 * Build court invoice "Labor Breakdown by Resource" table.
 * Aggregates hours/rates per user, matching the logic in InvoiceDetail.getCourtTime().
 */
function buildCourtTimeTable(tasks: Task[], invoice: Invoice): Content {
    // For finalized invoices with frozen court time data, use pre-resolved snapshot
    const fv = invoice.finalValues as FinalValues | null;
    if (invoice.invoiceStatus === 'F' && fv?.display?.courtTimeByResource) {
        const data = fv.display.courtTimeByResource.map(ct => ({
            user: ct.userName,
            hours: ct.hours,
            rate: 0, // Rate not stored in court time snapshot — use tasks
            cost: 0,
        }));

        // Recalculate rate/cost from frozen task data if available
        if (fv.tasks) {
            const tasksByUser = new Map<string, typeof fv.tasks>();
            for (const ft of fv.tasks.filter(t => !t.hideFromInvoice)) {
                const name = ft.assignedToName;
                if (!tasksByUser.has(name)) tasksByUser.set(name, []);
                tasksByUser.get(name)!.push(ft);
            }
            for (const row of data) {
                const userTasks = tasksByUser.get(row.user);
                if (userTasks?.length) {
                    row.rate = userTasks[0].rate;
                    row.cost = +(row.hours * row.rate).toFixed(2);
                }
            }
        }

        return createTable({
            columns: [
                { header: 'User', field: 'user', width: '*' },
                { header: 'Hours', field: 'hours', alignment: 'right' },
                { header: 'Rate', field: 'rate', alignment: 'right', isCurrency: true },
                { header: 'Cost', field: 'cost', alignment: 'right', isCurrency: true },
            ],
            data,
            showHeader: true,
            alternatingRows: false,
        });
    }

    const optionHelper = new OptionHelper();
    const billingIncrement = invoice.billingIncrement ?? optionHelper.getOptionValue(OptionHelper.BILLING_INCREMENT) as number;
    const roundingMethod = invoice.roundingMethod ?? optionHelper.getOptionValue(OptionHelper.ROUNDING_METHOD) as string;

    const filteredTasks = tasks.filter(t => !t.hideFromInvoice);
    const userIds = [...new Set(filteredTasks.map(t => t.assignedTo))];

    const matter = invoice.matterIds?.[0] ? CaseHelper.getMatterFromId(invoice.matterIds[0]) : null;

    const data = userIds.map(uid => {
        const user = find<User>('users', uid!);
        const rate = matter ? getBillableRate(uid!, matter) : (user?.billableRate ?? 0);
        const hours = filteredTasks
            .filter(t => t.assignedTo === uid)
            .reduce((sum, t) => sum + roundDurationToMinutes(t.elapsedTime, billingIncrement, roundingMethod) / 3_600_000, 0);
        const roundedHours = +hours.toFixed(2);
        return {
            user: user ? formatUserName(user) : '',
            hours: roundedHours,
            rate,
            cost: +(roundedHours * rate).toFixed(2),
        };
    });

    return createTable({
        columns: [
            { header: 'User', field: 'user', width: '*' },
            { header: 'Hours', field: 'hours', alignment: 'right' },
            { header: 'Rate', field: 'rate', alignment: 'right', isCurrency: true },
            { header: 'Cost', field: 'cost', alignment: 'right', isCurrency: true },
        ],
        data,
        showHeader: true,
        alternatingRows: false,
    });
}

/**
 * Build narrative (summary) content
 */
function buildNarrativeContent(tasks: Task[]): Content[] {
    const content: Content[] = [];
    const optionHelper = new OptionHelper();

    // Filter tasks
    const filteredTasks = tasks.filter(t => !t.hideFromInvoice);

    if (filteredTasks.length === 0) {
        return content;
    }

    // Narrative header
    const narrativeHeader = optionHelper.getOptionValue(OptionHelper.INVOICE_NARRATIVE_HEADER);
    content.push({
        text: narrativeHeader || 'Work We Did for You',
        style: 'narrativeHeader',
    });

    // Group tasks by date
    const dateGroups = new Map<string, Task[]>();
    for (const task of filteredTasks) {
        const date = formatDate(task.completionDate);
        if (!dateGroups.has(date)) {
            dateGroups.set(date, []);
        }
        dateGroups.get(date)!.push(task);
    }

    // Render each date group
    for (const [date, dateTasks] of dateGroups) {
        content.push({
            text: date,
            style: 'narrativeDate',
        });

        // Build narrative text for this date
        const narrativeItems: string[] = [];
        for (const task of dateTasks) {
            const taskAny = task as any;
            const initials = taskAny._finalAssignedToInitials ?? getUserInitials(task.assignedTo || task.createdBy);

            let notes = cleanText(task.invoiceNotes || task.title || '');

            // Ensure proper sentence ending
            if (notes && !['.', '!', '?'].includes(notes.trim().slice(-1))) {
                notes = notes + '.';
            }

            narrativeItems.push(`${initials} ${notes}`);
        }

        content.push({
            text: narrativeItems.join(' '),
            style: 'narrativeText',
        });
    }

    return content;
}

/**
 * Build expenses table content
 */
function buildExpensesTable(expenses: Expense[]): Content {
    if (expenses.length === 0) {
        return { text: '' };
    }

    const columns: PdfTableColumn[] = [
        { header: 'Date', field: 'date', width: 62, noWrap: true },
        { header: 'Paid To', field: 'payee', width: 110 },
        { header: 'Description', field: 'description', width: '*', isHtml: true },
        { header: 'Cost', field: 'cost', width: 75, alignment: 'right', isCurrency: true, noWrap: true },
    ];

    const data = expenses.map(expense => {
        const expenseAny = expense as any;
        return {
            date: formatDate(expense.expenseDate),
            payee: expenseAny._finalPayeeName ?? (expense.payee ? formatFMLFromId(expense.payee, false) : ''),
            description: expense.description || '',
            cost: expenseAny._finalTotal ?? expense.quantity * expense.cost,
        };
    });

    const totalCost = data.reduce((sum, row) => sum + (row.cost as number), 0);
    const footer = {
        date: '',
        payee: '',
        description: 'Total Expenses',
        cost: totalCost,
    };

    return createTable({
        columns,
        data,
        showHeader: true,
        alternatingRows: false,
        showTotals: true,
        footer,
    });
}

/**
 * Build flat fees table content
 */
function buildFlatFeesTable(flatFeeInfo: InvoiceInfo['flatFeeInfo']): Content {
    const earnedFees = flatFeeInfo.flatFees.filter(ff => ff.earnedThisPeriod);
    const earnedMilestones = flatFeeInfo.flatFeeMilestones.filter(ffm => ffm.earnedThisPeriod);

    if (earnedFees.length === 0 && earnedMilestones.length === 0) {
        return { text: '' };
    }

    const columns: PdfTableColumn[] = [
        { header: 'Description', field: 'description', width: '*' },
        { header: 'Amount', field: 'amount', width: 100, alignment: 'right', isCurrency: true },
    ];

    const data: Array<{ description: string; amount: number }> = [];

    for (const ff of earnedFees) {
        data.push({
            description: ff.description || 'Flat Fee',
            amount: ff.amount,
        });
    }

    for (const ffm of earnedMilestones) {
        data.push({
            description: ffm.description || 'Milestone',
            amount: ffm.amount,
        });
    }

    const totalAmount = data.reduce((sum, row) => sum + row.amount, 0);
    const footer = {
        description: 'Total Flat Fees',
        amount: totalAmount,
    };

    return createTable({
        columns,
        data,
        showHeader: true,
        alternatingRows: false,
        showTotals: true,
        footer,
    });
}

/**
 * Build QR code data for payment portal.
 *
 * The payment portal fetches the full invoice (including finalValues) from
 * /unportal/invoiceDetails, so the URL only needs identifiers and processor IDs.
 */
function buildQrCodeData(
    invoice: Invoice,
    invoiceInfo: InvoiceInfo,
    matter: Matter | null,
    lead: Lead | null,
    clients: Contact[]
): QrCodeData | null {
    // Check if payment portal is enabled
    if (import.meta.env.VITE_APP_PAYMENT_PORTAL !== 'on' || !paymentApi.usesPayments()) {
        return null;
    }

    if (!matter && !lead) {
        return null;
    }

    const isLawpay = paymentApi.isLawpay();

    // Processor IDs are needed for payment processing (not available on the invoice)
    const benefactorIds = CaseHelper.getBenefactors(matter ? [matter] : []);
    const benefactors = benefactorIds.map(cid => find<Contact>('contacts', cid)).filter(Boolean) as Contact[];
    const benefactorProcessorIds = benefactors.map(c => isLawpay ? c.processorContactId : c.id);
    const clientProcessorIds = clients.map(c => isLawpay ? c.processorContactId : c.id);

    const url = SOCKET_OPTIONS.payments + [
        `fid=${encodeBase62(getCurrentFirmId())}`,
        `iid=${encodeBase62(invoiceInfo.invoiceId)}`,
        `pid=${benefactorProcessorIds.concat(clientProcessorIds).join('|')}`,
    ].join('&');

    return {
        url,
        label: 'Make a Payment',
        size: 80,
    };
}

/**
 * Build trust accounting content.
 * For finalized invoices with frozen data, uses the pre-resolved trust snapshot.
 */
function buildTrustAccountingContent(
    invoice: Invoice,
    matter: Matter
): Content[] {
    // For finalized invoices with a frozen trust snapshot, use pre-resolved data
    const fv = invoice.finalValues as FinalValues | null;
    if (invoice.invoiceStatus === 'F' && fv?.trustAccounting) {
        return buildTrustAccountingContentFromFrozen(fv.trustAccounting);
    }

    // Get matter trust info
    const matterTrustInfo = FinancialHelper.getMatterTrustInfo(matter.id, invoice);

    // Get client trust infos
    const clientTrustInfos = invoice.clientIds.map(cid => {
        const clientInfo = FinancialHelper.getClientTrustInfo(cid, invoice);
        if (!clientInfo) return null;

        const clientName = formatFMLFromId(cid);

        return {
            clientName,
            startingBalance: clientInfo.startingBalance,
            transactionsThisPeriod: clientInfo.transactionsThisPeriod.map((tx: Transaction) => ({
                txDate: tx.txDate,
                txType: tx.txType,
                notes: tx.notes,
                amount: FinancialHelper.getPaymentAmount(tx, invoice.matterIds[0]),
                isNegative: FinancialHelper.isNegative(tx, [cid]),
                currentBalance: tx.currentBalance,
            })),
            endingBalance: clientInfo.endingBalance,
        };
    }).filter(Boolean) as Array<{
        clientName: string;
        startingBalance: number;
        transactionsThisPeriod: Array<{
            txDate: Date | string;
            txType: string;
            notes?: string;
            amount: number;
            isNegative: boolean;
            currentBalance: number;
        }>;
        endingBalance: number;
    }>;

    // Build trust accounting data
    const trustData = buildTrustAccountingData(
        {
            startingBalance: matterTrustInfo.startingBalance,
            transactionsThisPeriod: matterTrustInfo.transactionsThisPeriod.map((tx: Transaction) => ({
                txDate: tx.txDate,
                txType: tx.txType,
                notes: tx.notes as string | undefined,
                amount: FinancialHelper.getPaymentAmount(tx, matter.id),
                currentBalance: tx.currentBalance as number,
            })),
            endingBalance: matterTrustInfo.endingBalance,
        },
        clientTrustInfos,
        TX_TYPE_LABELS
    );

    // Check if there's any trust activity to show
    const hasCaseTrust = trustData.caseTrust &&
        (trustData.caseTrust.transactions.length > 0 || trustData.caseTrust.startingBalance > 0);
    const hasClientTrusts = trustData.clientTrusts.some(
        ct => ct.transactions.length > 0 || ct.startingBalance > 0
    );

    if (!hasCaseTrust && !hasClientTrusts) {
        return [];
    }

    return createTrustAccounting(trustData);
}

/**
 * Build trust accounting content from a frozen FinalTrustAccounting snapshot.
 */
function buildTrustAccountingContentFromFrozen(
    trustAccounting: FinalValues['trustAccounting']
): Content[] {
    const caseTrustData = trustAccounting.caseTrust ? {
        startingBalance: trustAccounting.caseTrust.beginningBalance,
        transactions: trustAccounting.caseTrust.transactions.map(t => ({
            date: t.date,
            type: t.typeLabel,
            notes: t.notes || '',
            amount: t.amount,
            runningBalance: t.runningBalance,
        })),
        endingBalance: trustAccounting.caseTrust.endingBalance,
    } : null;

    const clientTrustsData = trustAccounting.clientTrusts.map(ct => ({
        clientName: ct.name,
        startingBalance: ct.beginningBalance,
        transactions: ct.transactions.map(t => ({
            date: t.date,
            type: t.typeLabel,
            notes: t.notes || '',
            amount: t.amount,
            runningBalance: t.runningBalance,
        })),
        endingBalance: ct.endingBalance,
    }));

    const trustData = buildTrustAccountingData(
        caseTrustData ? {
            startingBalance: caseTrustData.startingBalance,
            transactionsThisPeriod: caseTrustData.transactions.map(t => ({
                txDate: t.date as string,
                txType: t.type,
                notes: t.notes,
                amount: t.amount,
                currentBalance: t.runningBalance,
            })),
            endingBalance: caseTrustData.endingBalance,
        } : { startingBalance: 0, transactionsThisPeriod: [], endingBalance: 0 },
        clientTrustsData.map(ct => ({
            clientName: ct.clientName,
            startingBalance: ct.startingBalance,
            transactionsThisPeriod: ct.transactions.map(t => ({
                txDate: t.date as string,
                txType: t.type,
                notes: t.notes,
                amount: t.amount,
                isNegative: t.amount < 0,
                currentBalance: t.runningBalance,
            })),
            endingBalance: ct.endingBalance,
        })),
        TX_TYPE_LABELS
    );

    const hasCaseTrust = trustData.caseTrust &&
        (trustData.caseTrust.transactions.length > 0 || trustData.caseTrust.startingBalance > 0);
    const hasClientTrusts = trustData.clientTrusts.some(
        ct => ct.transactions.length > 0 || ct.startingBalance > 0
    );

    if (!hasCaseTrust && !hasClientTrusts) {
        return [];
    }

    return createTrustAccounting(trustData);
}

/**
 * Generate an invoice PDF
 *
 * @param invoice - The invoice to generate
 * @param options - Generation options
 * @returns PDF data URL and filename
 */
export async function generateInvoicePdf(
    invoice: Invoice,
    options: InvoicePdfOptions
): Promise<PdfGenerationResult> {
    const { mode, courtInvoice = false } = options;
    const optionHelper = new OptionHelper();

    // Get invoice info
    const invoiceInfo = FinancialHelper.getInvoiceInfo(invoice);
    if (!invoiceInfo) {
        throw new Error('Failed to get invoice information');
    }

    // Get related entities
    const isLead = !!invoice.leadId;
    const clients = invoice.clientIds
        .map(cid => find<Contact>('contacts', cid))
        .filter(Boolean) as Contact[];
    const matter = isLead ? null : (CaseHelper.getMatterFromId(invoice.matterIds[0]) as Matter | null);
    const lead = isLead ? store.getState().leads.get(invoice.leadId!) : null;

    // Build filename
    const clientNames = clients.map(c => c.billingName || formatFML(c)).join(' and ');
    const matterName = matter?.name || lead?.name || '';
    const fileDate = DateUtil.formatDate(new Date(), false, true, false);
    const invoiceType = courtInvoice ? 'Court' : mode === 'N' ? 'Summary' : 'Detailed';
    const filename = `${fileDate} ${invoiceType} Invoice ${invoice.invoiceNumber} - ${clientNames} - ${matterName}.pdf`;

    // Get letterhead
    const letterheadHtml = optionHelper.getOptionValue<string>(OptionHelper.FIRM_LETTERHEAD);

    // Read configured font size (e.g. "12pt" -> 12)
    const configuredFontSize = optionHelper.getOptionValue<string>(OptionHelper.FONT_SIZE) || '10pt';
    const defaultFontSize = parseFloat(configuredFontSize) || 10;

    // Create PDF document
    const pageMargins = 36; // 0.5 inch
    const pdf = new PdfDocument({
        pageSize: 'LETTER',
        pageMargins,
        title: filename.replace('.pdf', ''),
        subject: `Invoice ${invoice.invoiceNumber}`,
        author: getCurrentFirm().legalName,
        keywords: 'receipt,invoice,payment',
        defaultFontSize,
    });

    // Add styles (scaled proportionally if font size differs from default 10pt)
    pdf.addStyles(getInvoiceStyles(courtInvoice, defaultFontSize));

    // === Build Document Content ===

    // 1. Letterhead — parse Quill HTML into native pdfmake content
    await loadFontsForPdf(letterheadHtml || '');
    const letterheadContent = parseQuillHtml(letterheadHtml || '');
    if (letterheadContent.length > 0) {
        pdf.addContent(...letterheadContent);
    } else {
        // Fallback to text-based header if parsing yields nothing
        const letterhead = parseLetterhead(letterheadHtml || '');
        pdf.addContent(...createHeader(letterhead));
    }

    // 2. Client info (left) and Account Summary (right)
    // For finalized invoices with frozen data, use pre-resolved display info
    const fv = invoice.finalValues as FinalValues | null;
    const hasFrozenData = invoice.invoiceStatus === 'F' && fv?.display;
    const clientInfo = hasFrozenData
        ? buildClientInfoFromFrozen(invoice, fv.display)
        : buildClientInfo(invoice, clients, matter, lead);

    // Build account summary rows with discount reason labels
    // Use pre-resolved display names from frozen snapshot when available
    const discountsForSummary = invoiceInfo.discounts.map(d => {
        const discountAny = d as any;
        return {
            reason: d.reason,
            reasonLabel: discountAny._finalReasonDisplay ?? LookupRegistry.getLookupById(d.reason)?.key ?? d.reason,
            amount: d.amount ?? 0,
        };
    });

    // Build payment descriptions — use pre-resolved from frozen snapshot when available
    const paymentsForSummary = invoiceInfo.payments
        .filter(tx => !tx.voidedDate && !tx.refusedDate && tx.status !== 'D')
        .map(tx => {
            const txAny = tx as any;
            return {
                txDate: tx.txDate,
                description: txAny._finalDescription ?? describeTransaction(tx),
                txType: tx.txType,
                amount: tx.amount,
            };
        });

    // Build adjustments, withdrawals, refunds with required amounts
    const adjustmentsForSummary = invoiceInfo.adjustments.map(a => ({
        notes: a.notes as string | undefined,
        amount: a.amount,
    }));

    const withdrawalsForSummary = invoiceInfo.withdrawals.map(w => ({
        notes: w.notes as string | undefined,
        amount: w.amount,
    }));

    const refundsForSummary = invoiceInfo.refunds.map(r => ({
        notes: r.notes as string | undefined,
        amount: r.amount,
    }));

    const accountSummaryRows = buildAccountSummaryRows({
        taskTotal: invoiceInfo.taskTotal,
        expenseTotal: invoiceInfo.expenseTotal,
        flatFeeTotal: invoiceInfo.flatFeeTotal,
        adjustments: adjustmentsForSummary,
        adjustmentTotal: invoiceInfo.adjustmentTotal,
        withdrawals: withdrawalsForSummary,
        withdrawalsTotal: invoiceInfo.withdrawalsTotal,
        refunds: refundsForSummary,
        refundTotal: invoiceInfo.refundTotal,
        discounts: discountsForSummary,
        discountTotal: invoiceInfo.discountTotal,
        currentInvoiceAmount: invoiceInfo.currentInvoiceAmount,
        pastDue: invoiceInfo.pastDue,
        currentBalance: invoiceInfo.currentBalance,
        payments: paymentsForSummary,
        paymentTotal: invoiceInfo.paymentTotal,
        amountDue: invoiceInfo.amountDue,
        isLead,
        taxTotal: invoiceInfo.taxTotal,
        interestTotal: invoiceInfo.interestTotal,
        taxLabel: invoiceInfo.taxLabel,
    });

    const accountSummary = createAccountSummary({ rows: accountSummaryRows });

    pdf.addContent(createTwoColumn(clientInfo, accountSummary, {
        widths: ['*', 280],
        columnGap: 20,
        margin: [0, 0, 0, 20],
    }));

    // 3. Invoice description (if any)
    if (invoice.description) {
        await loadFontsForPdf(invoice.description);
        const descContent = parseHtmlContent(invoice.description);
        if (descContent.length > 0) {
            pdf.addContent({
                stack: descContent,
                margin: [0, 0, 0, 15],
                italics: true,
                // Keep the user's entered indentation/leading spaces. Line
                // breaks come through as separate paragraphs / \n already. OWA-506.
                preserveLeadingSpaces: true,
            } as Content);
        }
    }

    // 4. Labor section
    if (mode === 'D') {
        // Detailed mode - table format
        if (invoiceInfo.tasks.length > 0) {
            pdf.addContent({
                text: 'Labor',
                style: 'sectionHeader',
                headlineLevel: 1,
            });
            pdf.addContent(buildDetailedLaborTable(invoiceInfo.tasks, invoice));
        }
    } else {
        // Narrative mode - story format
        pdf.addContent(...buildNarrativeContent(invoiceInfo.tasks));
    }

    // 4b. Court Invoice - Labor Breakdown by Resource
    if (courtInvoice && !isLead && invoiceInfo.tasks.length > 0) {
        pdf.addContent({
            text: 'Labor Breakdown by Resource',
            style: 'sectionHeader',
            headlineLevel: 1,
        });
        pdf.addContent(buildCourtTimeTable(invoiceInfo.tasks, invoice));
    }

    // 5. Flat Fees section (if any)
    if (invoiceInfo.flatFeeInfo.earnedAmount > 0) {
        pdf.addContent({
            text: 'Flat Fees',
            style: 'sectionHeader',
            headlineLevel: 1,
        });
        pdf.addContent(buildFlatFeesTable(invoiceInfo.flatFeeInfo));
    }

    // 6. Expenses section (if any)
    if (invoiceInfo.expenses.length > 0) {
        pdf.addContent({
            text: 'Expenses',
            style: 'sectionHeader',
            headlineLevel: 1,
        });
        pdf.addContent(buildExpensesTable(invoiceInfo.expenses));
    }

    // 7. Trust Accounting (for matters only, not leads)
    if (matter && !isLead) {
        const trustContent = buildTrustAccountingContent(invoice, matter);
        if (trustContent.length > 0) {
            pdf.addContent(...trustContent);
        }
    }

    // 8. QR Code for payment portal (at the very end)
    const qrCodeData = buildQrCodeData(invoice, invoiceInfo, matter, lead, clients);
    if (qrCodeData) {
        const qrResult = await createBarcodeSection(qrCodeData);
        // Register the QR code image with the document
        pdf.addImage(qrResult.imageName, qrResult.imageData);
        pdf.addContent(qrResult.content);
    }

    // Generate and return
    return pdf.generate(filename);
}

/**
 * Describe a transaction for the account summary
 */
function describeTransaction(tr: Transaction): string {
    if (tr.txType === 'SA') return 'Balance Forward';
    else if (tr.txType === 'PT') {
        if (tr.sourceType === 'M') return 'Payment from Case Trust';
        else return 'Payment from Client Trust';
    }
    else if (tr.txType === 'PC') {
        const payerName = formatFMLFromId(tr.source!);
        return `Payment from ${payerName}`;
    }
    else if (['BA', 'ST', 'SA'].includes(tr.txType)) return 'Adjustment';
    else if (tr.txType === 'TT') return 'Transfer to Trust';
    else return 'Payment';
}

export default generateInvoicePdf;
